import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { SortDirection, VisitSortOrder } from "@/modules/dashboard/Panel/utils/sortUtils"
import { RootState } from "@core/store"

export type DashboardPanelKey = "res" | "wait" | "treat" | "comp" | string

interface DashboardPanelSortState {
  orders: Record<DashboardPanelKey, VisitSortOrder>
  directions: Record<DashboardPanelKey, SortDirection>
  insuranceTypeSortEnabled: Record<DashboardPanelKey, boolean>
  unchartedFirstEnabled: Record<DashboardPanelKey, boolean>
}

const initialState: DashboardPanelSortState = {
  orders: {
    res: "default",
    wait: "default",
    treat: "default",
    comp: "default",
  },
  directions: {
    res: "asc",
    wait: "asc",
    treat: "asc",
    comp: "asc",
  },
  insuranceTypeSortEnabled: {
    res: false,
    wait: false,
    treat: false,
    comp: false,
  },
  unchartedFirstEnabled: {
    res: false,
    wait: false,
    treat: false,
    comp: false,
  },
}

const dashboardPanelSortSlice = createSlice({
  name: "dashboardPanelSort",
  initialState,
  reducers: {
    setPanelSortOrder: (state, action: PayloadAction<{ panelKey: DashboardPanelKey; sortOrder: VisitSortOrder }>) => {
      const { panelKey, sortOrder } = action.payload
      state.orders[panelKey] = sortOrder
    },
    setPanelSortDirection: (state, action: PayloadAction<{ panelKey: DashboardPanelKey; sortDirection: SortDirection }>) => {
      const { panelKey, sortDirection } = action.payload
      state.directions[panelKey] = sortDirection
    },
    setPanelInsuranceTypeSortEnabled: (state, action: PayloadAction<{ panelKey: DashboardPanelKey; enabled: boolean }>) => {
      const { panelKey, enabled } = action.payload
      state.insuranceTypeSortEnabled[panelKey] = enabled
    },
    setPanelUnchartedFirstEnabled: (state, action: PayloadAction<{ panelKey: DashboardPanelKey; enabled: boolean }>) => {
      const { panelKey, enabled } = action.payload
      state.unchartedFirstEnabled[panelKey] = enabled
    },
  },
})

export const { setPanelSortOrder, setPanelSortDirection, setPanelInsuranceTypeSortEnabled, setPanelUnchartedFirstEnabled } =
  dashboardPanelSortSlice.actions

export const selectPanelSortOrder = (state: RootState, panelKey: DashboardPanelKey): VisitSortOrder => {
  return state.dashboardPanelSort?.orders?.[panelKey] ?? "default"
}

export const selectPanelSortDirection = (state: RootState, panelKey: DashboardPanelKey): SortDirection => {
  return state.dashboardPanelSort?.directions?.[panelKey] ?? "asc"
}

export const selectPanelInsuranceTypeSortEnabled = (state: RootState, panelKey: DashboardPanelKey): boolean => {
  return state.dashboardPanelSort?.insuranceTypeSortEnabled?.[panelKey] ?? false
}

export const selectPanelUnchartedFirstEnabled = (state: RootState, panelKey: DashboardPanelKey): boolean => {
  return state.dashboardPanelSort?.unchartedFirstEnabled?.[panelKey] ?? false
}

export default dashboardPanelSortSlice.reducer
